import type { Message, AgentMessageFragment } from "@/hooks/use-stream-conversation";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";

/** Info about a pending todo in the block */
export type PendingTodoInfo = {
    todoId: string;
    title: string;
};

export type GroupedMessage =
    | { type: "user"; message: Message }
    | { type: "agent"; message: Message }
    | {
          type: "todo-group";
          todoId: string;
          todoTitle: string;
          todoStatus: AgentResponseBlock_TodoItem_Phase;
          fragments: AgentMessageFragment[];
          /** Unique identifier for the block this todo belongs to */
          blockId: string;
          /** Whether this is the first todo in its block (show header) */
          isFirstInBlock: boolean;
          /** Whether this is the last todo in its block */
          isLastInBlock: boolean;
          /** Total todos in this block */
          blockTotalTodos: number;
          /** Completed todos in this block */
          blockCompletedTodos: number;
          /** Pending todos that come after this one in the block */
          pendingTodos: PendingTodoInfo[];
      }
    | { type: "agent-ungrouped"; fragments: AgentMessageFragment[]; messageId: number };

/**
 * Tracks todo blocks - groups of todos created together.
 * A new block starts when:
 * 1. A todo is first seen with PENDING status (batch creation via todo_write/todo_reset)
 * 2. After a clear operation (CLEARED status on todos)
 */
type TodoBlock = {
    id: string;
    todoIds: string[];
    todoTitles: Map<string, string>; // todoId -> title
    completedTodoIds: Set<string>;
    inProgressTodoIds: Set<string>;
};

/**
 * Groups messages by todo items and organizes them into blocks.
 *
 * Todos are grouped into blocks based on when they were created together.
 * The agent typically creates multiple todos at once (via todo_reset or todo_write),
 * and clears them together (via todo_clear or todo_reset).
 *
 * Each block gets its own header showing progress (completed/total).
 *
 * @param messages - The messages to group
 */
export function groupMessagesByTodo(messages: Message[]): GroupedMessage[] {
    // First pass: identify todo blocks by tracking when todos are first created
    const todoBlockLookup = identifyTodoBlocks(messages);

    // Second pass: group messages and assign block info
    return groupMessagesWithBlocks(messages, todoBlockLookup);
}

/**
 * First pass: Identify todo blocks by tracking creation patterns.
 * A block is a set of todos that were created together (first seen with PENDING status
 * in the same message or consecutive messages before any IN_PROGRESS).
 */
function identifyTodoBlocks(messages: Message[]): Map<string, TodoBlock> {
    const todoBlocks = new Map<string, TodoBlock>();
    const todoToBlock = new Map<string, string>(); // todoId -> blockId
    const seenTodos = new Set<string>();

    let currentBlockId: string | null = null;
    let currentBlockTodos: string[] = [];
    let currentBlockTitles = new Map<string, string>();
    let blockCounter = 0;

    // Track if we're in a "creation phase" - consecutive PENDING todos
    let inCreationPhase = false;

    const finalizeBlock = () => {
        if (currentBlockId && currentBlockTodos.length > 0) {
            todoBlocks.set(currentBlockId, {
                id: currentBlockId,
                todoIds: [...currentBlockTodos],
                todoTitles: new Map(currentBlockTitles),
                completedTodoIds: new Set(),
                inProgressTodoIds: new Set(),
            });
        }
        currentBlockId = null;
        currentBlockTodos = [];
        currentBlockTitles = new Map();
    };

    for (const message of messages) {
        if (message.type === "user") {
            // User message ends any creation phase
            if (inCreationPhase && currentBlockTodos.length > 0) {
                finalizeBlock();
            }
            inCreationPhase = false;
            continue;
        }

        // Agent message
        for (const fragment of message.fragments) {
            if (fragment.type !== "todoItem") continue;

            const todoId = fragment.todoItemId;
            const status = fragment.status;

            if (status === AgentResponseBlock_TodoItem_Phase.CLEARED) {
                // Clearing ends the current block
                if (inCreationPhase && currentBlockTodos.length > 0) {
                    finalizeBlock();
                }
                inCreationPhase = false;
                continue;
            }

            if (!seenTodos.has(todoId)) {
                // First time seeing this todo - it's being created
                seenTodos.add(todoId);

                if (
                    status === AgentResponseBlock_TodoItem_Phase.PENDING ||
                    status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS
                ) {
                    // Start or continue creation phase
                    if (!inCreationPhase) {
                        // Start new block
                        blockCounter++;
                        currentBlockId = `block-${blockCounter}`;
                        currentBlockTodos = [];
                        currentBlockTitles = new Map();
                        inCreationPhase = true;
                    }
                    currentBlockTodos.push(todoId);
                    currentBlockTitles.set(todoId, fragment.title);
                    todoToBlock.set(todoId, currentBlockId!);
                }
            } else {
                // Already seen this todo - check if it's transitioning to IN_PROGRESS
                if (status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS) {
                    // Transition to IN_PROGRESS ends creation phase
                    if (inCreationPhase && currentBlockTodos.length > 0) {
                        finalizeBlock();
                    }
                    inCreationPhase = false;
                }
            }
        }
    }

    // Finalize any remaining block
    if (inCreationPhase && currentBlockTodos.length > 0) {
        finalizeBlock();
    }

    // Second pass to track completed and in-progress todos
    for (const message of messages) {
        if (message.type === "user") continue;

        for (const fragment of message.fragments) {
            if (fragment.type !== "todoItem") continue;

            const todoId = fragment.todoItemId;
            const blockId = todoToBlock.get(todoId);

            if (blockId) {
                const block = todoBlocks.get(blockId);
                if (block) {
                    if (fragment.status === AgentResponseBlock_TodoItem_Phase.DONE) {
                        block.completedTodoIds.add(todoId);
                    }
                    if (fragment.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS) {
                        block.inProgressTodoIds.add(todoId);
                    }
                    // Update title if we have a newer one
                    if (fragment.title) {
                        block.todoTitles.set(todoId, fragment.title);
                    }
                }
            }
        }
    }

    // Create a map from todoId to block for easy lookup
    const todoBlockLookup = new Map<string, TodoBlock>();
    for (const block of todoBlocks.values()) {
        for (const todoId of block.todoIds) {
            todoBlockLookup.set(todoId, block);
        }
    }

    return todoBlockLookup;
}

/**
 * Second pass: Group messages and assign block information to todo groups.
 */
function groupMessagesWithBlocks(messages: Message[], todoBlockLookup: Map<string, TodoBlock>): GroupedMessage[] {
    const grouped: GroupedMessage[] = [];
    let currentTodoGroup: {
        todoId: string;
        todoTitle: string;
        todoStatus: AgentResponseBlock_TodoItem_Phase;
        fragments: AgentMessageFragment[];
        blockId: string;
    } | null = null;
    let ungroupedFragments: AgentMessageFragment[] = [];
    let currentMessageId = 0;

    // Track which todos we've seen in the output to determine first/last in block
    const processedTodosInBlock = new Map<string, string[]>(); // blockId -> todoIds processed

    const flushUngrouped = () => {
        if (ungroupedFragments.length > 0) {
            grouped.push({ type: "agent-ungrouped", fragments: [...ungroupedFragments], messageId: currentMessageId });
            ungroupedFragments = [];
        }
    };

    const flushTodoGroup = () => {
        if (currentTodoGroup) {
            const block = todoBlockLookup.get(currentTodoGroup.todoId);
            const blockId = block?.id || currentTodoGroup.blockId;

            // Track this todo as processed in its block
            if (!processedTodosInBlock.has(blockId)) {
                processedTodosInBlock.set(blockId, []);
            }
            const processedInBlock = processedTodosInBlock.get(blockId)!;
            const isFirstInBlock = processedInBlock.length === 0;
            processedInBlock.push(currentTodoGroup.todoId);

            // Calculate block stats
            const blockTotalTodos = block?.todoIds.length || 1;
            const blockCompletedTodos = block?.completedTodoIds.size || 0;

            // Determine if this is the last todo in the block
            // We'll update this later in a post-processing step
            const isLastInBlock = processedInBlock.length === blockTotalTodos;

            // Calculate pending todos (todos in block that haven't started yet)
            const pendingTodos: PendingTodoInfo[] = [];
            if (block) {
                const currentIndex = block.todoIds.indexOf(currentTodoGroup.todoId);
                // Find todos that come after this one and are still pending
                for (let i = currentIndex + 1; i < block.todoIds.length; i++) {
                    const todoId = block.todoIds[i];
                    // A todo is pending if it's not completed and not in progress
                    if (!block.completedTodoIds.has(todoId) && !block.inProgressTodoIds.has(todoId)) {
                        pendingTodos.push({
                            todoId,
                            title: block.todoTitles.get(todoId) || "Pending task",
                        });
                    }
                }
            }

            grouped.push({
                type: "todo-group",
                todoId: currentTodoGroup.todoId,
                todoTitle: currentTodoGroup.todoTitle,
                todoStatus: currentTodoGroup.todoStatus,
                fragments: currentTodoGroup.fragments,
                blockId,
                isFirstInBlock,
                isLastInBlock,
                blockTotalTodos,
                blockCompletedTodos,
                pendingTodos,
            });
            currentTodoGroup = null;
        }
    };

    for (const message of messages) {
        if (message.type === "user") {
            // Flush any pending groups
            flushUngrouped();
            flushTodoGroup();
            // Add user message
            grouped.push({ type: "user", message });
            continue;
        }

        // Agent message
        const agentMessage = message;
        currentMessageId = agentMessage.id;

        for (const fragment of agentMessage.fragments) {
            // Check if this fragment is a todo item with IN_PROGRESS status
            if (fragment.type === "todoItem" && fragment.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS) {
                // Flush any ungrouped fragments before starting new todo
                flushUngrouped();
                // Flush previous todo group if exists
                flushTodoGroup();

                // Get block info for this todo
                const block = todoBlockLookup.get(fragment.todoItemId);

                // Start new todo group
                currentTodoGroup = {
                    todoId: fragment.todoItemId,
                    todoTitle: fragment.title,
                    todoStatus: fragment.status,
                    fragments: [],
                    blockId: block?.id || `orphan-${fragment.todoItemId}`,
                };
            } else if (fragment.type === "todoItem" && fragment.status === AgentResponseBlock_TodoItem_Phase.DONE) {
                // Update status of current todo group if it matches
                if (currentTodoGroup && currentTodoGroup.todoId === fragment.todoItemId) {
                    currentTodoGroup.todoStatus = fragment.status;
                    // Flush the completed todo group
                    flushTodoGroup();
                }
            } else if (fragment.type !== "todoItem") {
                // Skip text fragments that are just announcing todo state changes
                const isSystemTodoMessage =
                    fragment.type === "text" &&
                    fragment.content &&
                    fragment.content.toLowerCase().includes("marking") &&
                    fragment.content.toLowerCase().includes("todo") &&
                    (fragment.content.toLowerCase().includes("in progress") ||
                        fragment.content.toLowerCase().includes("done"));

                if (isSystemTodoMessage) {
                    continue;
                }

                // Check if this is a summary - summaries should never be grouped into todos
                const summaryRegex = /^#+\s*summary(\s|\n|$)/i;
                const isSummary = fragment.type === "text" && summaryRegex.exec(fragment.content) !== null;

                if (isSummary) {
                    // Flush any active todo group before the summary
                    flushTodoGroup();
                    // Add summary to ungrouped
                    ungroupedFragments.push(fragment);
                } else {
                    // Regular fragment (not a todo item or summary)
                    if (currentTodoGroup) {
                        // Add to current todo group
                        currentTodoGroup.fragments.push(fragment);
                    } else {
                        // No active todo group, add to ungrouped
                        ungroupedFragments.push(fragment);
                    }
                }
            }
        }
    }

    // Flush any remaining fragments
    flushUngrouped();
    flushTodoGroup();

    // Post-process to update isLastInBlock correctly
    // We need to look ahead to see if there are more todos in the same block
    const blockLastTodoIndex = new Map<string, number>();
    for (let i = 0; i < grouped.length; i++) {
        const group = grouped[i];
        if (group.type === "todo-group") {
            blockLastTodoIndex.set(group.blockId, i);
        }
    }

    // Update isLastInBlock based on actual last occurrence
    for (let i = 0; i < grouped.length; i++) {
        const group = grouped[i];
        if (group.type === "todo-group") {
            const lastIndex = blockLastTodoIndex.get(group.blockId);
            (group as { isLastInBlock: boolean }).isLastInBlock = i === lastIndex;
        }
    }

    return grouped;
}
